import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Edit, Trash } from 'lucide-react';

type ClientData = {
  id: number;
  name: string;
  email: string;
  spent: number;
  status: 'active' | 'pending' | 'completed';
};

const DataTable: React.FC = () => {
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const clients: ClientData[] = [
    { id: 1, name: 'John Anderson', email: 'john@example.com', spent: 650, status: 'active' },
    { id: 2, name: 'Lisa Wang', email: 'lisa@example.com', spent: 1250, status: 'active' },
    { id: 3, name: 'Michael Peterson', email: 'michael@example.com', spent: 780, status: 'active' },
    { id: 4, name: 'Sarah Johnson', email: 'sarah@example.com', spent: 2100, status: 'completed' },
  ];

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const sortedClients = [...clients].sort((a, b) => {
    const aValue = a[sortBy as keyof ClientData];
    const bValue = b[sortBy as keyof ClientData];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc' 
        ? aValue.localeCompare(bValue) 
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });

  const tableVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    },
    hover: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      transition: { duration: 0.1 }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-accent text-background';
      case 'pending': return 'bg-warning text-background';
      case 'completed': return 'bg-success text-background';
      default: return 'bg-white/10 text-white';
    }
  };

  return (
    <motion.div 
      className="rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 overflow-hidden w-full"
      variants={tableVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="p-4 border-b border-white/10 flex justify-between items-center">
        <h3 className="font-semibold">Client History</h3>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-white/70">4 clients</span>
          <motion.button 
            className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            View All
          </motion.button>
        </div>
      </div>
      
      <div className="overflow-x-auto w-full">
        <table className="w-full min-w-[650px]">
          <thead className="text-left bg-white/5">
            <tr>
              <th className="px-4 py-3 text-xs font-semibold text-white/70 uppercase cursor-pointer" onClick={() => handleSort('name')}>
                <div className="flex items-center space-x-1">
                  <span>Name</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${sortBy === 'name' && sortOrder === 'desc' ? 'rotate-180' : ''}`} />
                </div>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-white/70 uppercase cursor-pointer" onClick={() => handleSort('email')}>
                <div className="flex items-center space-x-1">
                  <span>Email</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${sortBy === 'email' && sortOrder === 'desc' ? 'rotate-180' : ''}`} />
                </div>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-white/70 uppercase cursor-pointer" onClick={() => handleSort('spent')}>
                <div className="flex items-center space-x-1">
                  <span>Lifetime Value</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${sortBy === 'spent' && sortOrder === 'desc' ? 'rotate-180' : ''}`} />
                </div>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-white/70 uppercase cursor-pointer" onClick={() => handleSort('status')}>
                <div className="flex items-center space-x-1">
                  <span>Status</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${sortBy === 'status' && sortOrder === 'desc' ? 'rotate-180' : ''}`} />
                </div>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-white/70 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedClients.map((client) => (
              <motion.tr 
                key={client.id}
                className="border-t border-white/5"
                variants={rowVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center text-accent font-medium">
                      {client.name.charAt(0)}
                    </div>
                    <span>{client.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-white/70">{client.email}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-white/90">${client.spent.toLocaleString()}</div>
                  <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mt-1">
                    <motion.div 
                      className="h-full bg-accent"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(100, client.spent / 20)}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(client.status)}`}>
                    {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <motion.button 
                      className="p-1 rounded bg-white/5 hover:bg-white/10"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Edit size={14} />
                    </motion.button>
                    <motion.button 
                      className="p-1 rounded bg-white/5 hover:bg-error/90"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Trash size={14} />
                    </motion.button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

export default DataTable;